import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  integrationsApi,
  type IntegrationProvider,
  type Integrations,
  type LinearTeam,
  type Signal,
  type Webhook,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { DataTable, type DataTableColumn } from "@/components/ui/data-table";
import { BrandLogo, SETTINGS_BUTTONS, SettingsBlock, SettingsPane, SettingsRow, SettingsSection, StatusPill } from "./SettingsLayout";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Icon } from "@/components/ui/icon";
import { cn, timeAgo } from "@/lib/utils";
import { PENDING_INTEGRATION_KEY } from "@/pages/IntegrationCallbackPage";

const DOCS_URL = "https://peersheep.com/docs/api";

// D1 returns "YYYY-MM-DD HH:MM:SS" in UTC with no zone marker
const utc = (s: string) => (s.includes("T") ? s : s.replace(" ", "T") + "Z");

const SIGNAL_OPTIONS: { value: Signal; label: string }[] = [
  { value: "high", label: "High signal only" },
  { value: "medium", label: "Medium and high" },
  { value: "low", label: "Every change" },
];

function SignalSelect({ value, onChange, disabled }: { value: Signal; onChange: (v: Signal) => void; disabled?: boolean }) {
  return (
    <Select value={value} onValueChange={(v) => onChange(v as Signal)} disabled={disabled}>
      <SelectTrigger className="w-44">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {SIGNAL_OPTIONS.map((o) => (
          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function ConnectionStatus({ status, lastError }: { status: string | null; lastError: string | null }) {
  if (status === "error") return <StatusPill tone="error" title={lastError ?? undefined}>Needs attention</StatusPill>;
  return <StatusPill tone="ok">Connected</StatusPill>;
}

export function IntegrationsTab() {
  const [data, setData] = useState<Integrations | null>(null);
  const [connecting, setConnecting] = useState<IntegrationProvider | null>(null);
  const [teams, setTeams] = useState<LinearTeam[] | null>(null);
  const [disconnecting, setDisconnecting] = useState<IntegrationProvider | null>(null);

  // Webhooks
  const [hookUrl, setHookUrl] = useState("");
  const [hookSignal, setHookSignal] = useState<Signal>("medium");
  const [isAddingHook, setIsAddingHook] = useState(false);
  const [shownSecret, setShownSecret] = useState<string | null>(null);
  const [deletingHook, setDeletingHook] = useState<Webhook | null>(null);

  const [loadError, setLoadError] = useState<string | null>(null);

  const load = () => {
    setLoadError(null);
    integrationsApi.list().then((res) => {
      if ("data" in res && res.data) setData(res.data);
      else setLoadError("error" in res ? res.error.message : "Something went wrong");
    });
  };

  useEffect(load, []);

  // Linear teams, once connected, so the team can be picked or changed
  useEffect(() => {
    if (!data?.linear.connected) return;
    integrationsApi.linearTeams().then((res) => {
      if ("data" in res && res.data) setTeams(res.data);
    });
  }, [data?.linear.connected]);

  const connect = async (provider: IntegrationProvider) => {
    setConnecting(provider);
    const res = await integrationsApi.connect(provider);
    if ("data" in res && res.data) {
      sessionStorage.setItem(PENDING_INTEGRATION_KEY, provider);
      window.location.href = res.data.url;
      return;
    }
    if ("error" in res) toast.error(res.error.message);
    setConnecting(null);
  };

  const update = async (provider: IntegrationProvider, patch: { minSignal?: Signal; teamId?: string }) => {
    const res = await integrationsApi.update(provider, patch);
    if ("data" in res && res.data) {
      setData((d) => (d ? { ...d, [provider]: res.data } : d));
      toast.success("Saved");
    } else if ("error" in res) {
      toast.error(res.error.message);
    }
  };

  const disconnect = async () => {
    if (!disconnecting) return;
    const res = await integrationsApi.disconnect(disconnecting);
    if ("data" in res) {
      const fresh = await integrationsApi.list();
      if ("data" in fresh && fresh.data) setData(fresh.data);
      toast.success(`${disconnecting === "slack" ? "Slack" : "Linear"} disconnected`);
    } else if ("error" in res) {
      toast.error(res.error.message);
    }
    setDisconnecting(null);
  };

  const testSlack = async () => {
    const res = await integrationsApi.testSlack();
    if ("data" in res && res.data?.sent) toast.success("Test message sent");
    else toast.error("Slack didn't accept the message. Try reconnecting.");
  };

  const addWebhook = async () => {
    if (!hookUrl.trim()) return;
    setIsAddingHook(true);
    const res = await integrationsApi.createWebhook(hookUrl.trim(), hookSignal);
    if ("data" in res && res.data) {
      const { webhook, secret } = res.data;
      setData((d) => (d ? { ...d, webhooks: [...d.webhooks, webhook] } : d));
      setShownSecret(secret);
      setHookUrl("");
    } else if ("error" in res) {
      toast.error(res.error.message);
    }
    setIsAddingHook(false);
  };

  const updateWebhook = async (id: string, patch: { enabled?: boolean; minSignal?: Signal }) => {
    const res = await integrationsApi.updateWebhook(id, patch);
    if ("data" in res && res.data) {
      const updated = res.data;
      setData((d) => (d ? { ...d, webhooks: d.webhooks.map((w) => (w.id === id ? updated : w)) } : d));
    } else if ("error" in res) {
      toast.error(res.error.message);
    }
  };

  const testWebhook = async (id: string) => {
    const res = await integrationsApi.testWebhook(id);
    if ("data" in res && res.data) {
      const { status, delivered } = res.data;
      if (delivered) toast.success(`Delivered (HTTP ${status})`);
      else toast.error(status ? `Your endpoint answered HTTP ${status}` : "Couldn't reach your endpoint");
      const fresh = await integrationsApi.list();
      if ("data" in fresh && fresh.data) setData(fresh.data);
    }
  };

  const revealSecret = async (id: string) => {
    const res = await integrationsApi.webhookSecret(id);
    if ("data" in res && res.data) setShownSecret(res.data.secret);
    else if ("error" in res) toast.error(res.error.message);
  };

  const deleteWebhook = async () => {
    if (!deletingHook) return;
    const res = await integrationsApi.deleteWebhook(deletingHook.id);
    if ("data" in res) {
      setData((d) => (d ? { ...d, webhooks: d.webhooks.filter((w) => w.id !== deletingHook.id) } : d));
      toast.success("Webhook deleted");
    } else if ("error" in res) {
      toast.error(res.error.message);
    }
    setDeletingHook(null);
  };

  if (!data && loadError) {
    return (
      <SettingsPane title="Integrations" description="Send alerts to the tools your team already uses">
        <SettingsSection
          icon="alert-circle"
          title="Couldn't load your integrations"
          description={loadError}
          actions={<Button size="sm" variant="outline" onClick={load}>Retry</Button>}
        />
      </SettingsPane>
    );
  }

  if (!data) return <IntegrationsSkeleton />;

  const { slack, linear, webhooks } = data;
  const connectedCount = [slack.connected, linear.connected].filter(Boolean).length + webhooks.filter((w) => w.enabled).length;

  const webhookColumns: DataTableColumn<Webhook>[] = [
    {
      id: "endpoint",
      header: "Endpoint",
      cell: (w) => {
        const ok = w.lastStatus !== null && w.lastStatus >= 200 && w.lastStatus < 300;
        return (
          <div className={cn("min-w-0", !w.enabled && "opacity-60")}>
            <p className="truncate font-mono text-[11px]" title={w.url}>{w.url}</p>
            <p className="mt-0.5 flex items-center gap-1.5 font-mono text-[11px] text-muted-foreground">
              {w.lastDeliveredAt ? (
                <>
                  <span className={cn("h-1.5 w-1.5 rounded-full", ok ? "bg-emerald-500" : "bg-red-500")} />
                  <span className={ok ? undefined : "text-red-500"}>{w.lastStatus ? `HTTP ${w.lastStatus}` : "Unreachable"}</span>
                  · {timeAgo(utc(w.lastDeliveredAt))}
                </>
              ) : (
                <>
                  <span className="h-1.5 w-1.5 rounded-full border border-muted-foreground/60" />
                  No deliveries yet
                </>
              )}
            </p>
          </div>
        );
      },
    },
    {
      id: "signal",
      header: "Sends",
      width: "12rem",
      cell: (w) => <SignalSelect value={w.minSignal} onChange={(v) => void updateWebhook(w.id, { minSignal: v })} />,
    },
    {
      id: "enabled",
      header: "On",
      width: "4.5rem",
      cell: (w) => <Switch checked={w.enabled} onCheckedChange={(v) => void updateWebhook(w.id, { enabled: v })} aria-label="Enabled" />,
    },
    {
      id: "actions",
      header: <span className="sr-only">Actions</span>,
      width: "7.5rem",
      align: "right",
      cell: (w) => (
        <div className="flex items-center justify-end gap-0.5 transition-opacity md:opacity-0 md:group-hover:opacity-100 md:focus-within:opacity-100">
          <Button variant="ghost" size="icon-sm" onClick={() => void testWebhook(w.id)} aria-label="Send test event" title="Send test event">
            <Icon name="sent" size={13} />
          </Button>
          <Button variant="ghost" size="icon-sm" onClick={() => void revealSecret(w.id)} aria-label="Show signing secret" title="Signing secret">
            <Icon name="key-01" size={13} />
          </Button>
          <Button
            variant="ghost" size="icon-sm" className="text-muted-foreground hover:text-destructive"
            onClick={() => setDeletingHook(w)} aria-label="Delete webhook" title="Delete webhook"
          >
            <Icon name="delete-02" size={13} />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <SettingsPane
      title="Integrations"
      description={connectedCount > 0
        ? `${connectedCount} active · alerts flow to the tools your team already uses`
        : "Send alerts to the tools your team already uses"}
    >
      {/* ── Slack ── */}
      <SettingsSection
        icon={<BrandLogo domain="slack.com" name="Slack" fallbackIcon="slack" />}
        title="Slack"
        description="Post alerts to a channel of your choice"
        badge={slack.connected && <ConnectionStatus status={slack.status} lastError={slack.lastError} />}
        actions={!slack.connected ? (
          <Button size="sm" onClick={() => void connect("slack")} disabled={!slack.available || connecting === "slack"}>
            {connecting === "slack"
              ? <Icon name="refresh" size={12} className="animate-spin" />
              : <>Add to Slack</>}
          </Button>
        ) : (
          <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-destructive" onClick={() => setDisconnecting("slack")}>
            Disconnect
          </Button>
        )}
      >
        {!slack.connected ? (
          <SettingsBlock>
            <p className="font-sans text-xs text-muted-foreground">
              {slack.available
                ? "Slack asks which channel to post to, then alerts arrive there with the summary, what changed and a link back."
                : "The Slack app isn't set up on this server yet."}
            </p>
          </SettingsBlock>
        ) : (
          <>
            {slack.status === "error" && slack.lastError && <ErrorBlock message={slack.lastError} />}
            <SettingsRow
              label="Channel"
              hint={<>Posting to <span className="font-medium text-foreground">{slack.channel ?? "your channel"}</span>{slack.externalName && <> in {slack.externalName}</>}</>}
            >
              <Button variant="outline" size="sm" onClick={() => void testSlack()}>
                <Icon name="sent" size={12} className="mr-1.5" />Send test
              </Button>
              <Button variant="outline" size="sm" onClick={() => void connect("slack")} disabled={connecting === "slack"}>
                Change
              </Button>
            </SettingsRow>
            <SettingsRow label="Send" hint="With the daily digest on, medium and low changes wait for the digest instead.">
              <SignalSelect value={slack.minSignal} onChange={(v) => void update("slack", { minSignal: v })} />
            </SettingsRow>
          </>
        )}
      </SettingsSection>

      {/* ── Linear ── */}
      <SettingsSection
        icon={<BrandLogo domain="linear.app" name="Linear" fallbackIcon="flow-connection" />}
        title="Linear"
        description="Turn important competitor moves into issues for your team"
        badge={linear.connected && <ConnectionStatus status={linear.status} lastError={linear.lastError} />}
        actions={!linear.connected ? (
          <Button size="sm" onClick={() => void connect("linear")} disabled={!linear.available || connecting === "linear"}>
            {connecting === "linear"
              ? <Icon name="refresh" size={12} className="animate-spin" />
              : <>Connect</>}
          </Button>
        ) : (
          <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-destructive" onClick={() => setDisconnecting("linear")}>
            Disconnect
          </Button>
        )}
      >
        {!linear.connected ? (
          <SettingsBlock>
            <p className="font-sans text-xs text-muted-foreground">
              {linear.available
                ? "Peersheep files an issue for each change that meets your threshold, with the summary, the before and after, and links back. You can also file any alert by hand from its page."
                : "The Linear app isn't set up on this server yet."}
            </p>
          </SettingsBlock>
        ) : (
          <>
            {linear.status === "error" && linear.lastError && <ErrorBlock message={linear.lastError} />}
            <SettingsRow
              label="Team"
              hint={linear.team
                ? <>Issues go to <span className="font-medium text-foreground">{linear.team.name}</span> in {linear.externalName ?? "Linear"}</>
                : <span className="text-amber-500">Pick a team so Peersheep knows where to file issues.</span>}
            >
              <Select
                value={linear.team?.id ?? ""}
                onValueChange={(teamId) => void update("linear", { teamId })}
                disabled={!teams}
              >
                <SelectTrigger className="w-44">
                  <SelectValue placeholder={teams ? "Pick a team" : "Loading…"} />
                </SelectTrigger>
                <SelectContent>
                  {(teams ?? []).map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.name} ({t.key})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </SettingsRow>
            <SettingsRow label="File issues for" hint="Which alerts become Linear issues automatically.">
              <SignalSelect value={linear.minSignal} onChange={(v) => void update("linear", { minSignal: v })} />
            </SettingsRow>
          </>
        )}
      </SettingsSection>

      {/* ── Webhooks ── */}
      <SettingsSection
        icon="webhook"
        title="Webhooks"
        description={<>Send signed alerts to Zapier, Make, n8n or your own code. <a href={DOCS_URL} target="_blank" rel="noreferrer" className="text-foreground underline underline-offset-2">Docs</a></>}
      >
        <SettingsBlock className="space-y-3">
          <div className="flex flex-col gap-2 sm:flex-row">
            <Input
              type="url"
              placeholder="https://hooks.zapier.com/…"
              value={hookUrl}
              onChange={(e) => setHookUrl(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && void addWebhook()}
              className="sm:flex-1"
            />
            <div className="flex gap-2">
              <SignalSelect value={hookSignal} onChange={setHookSignal} />
              <Button onClick={() => void addWebhook()} disabled={isAddingHook || !hookUrl.trim()}>
                {isAddingHook ? <Icon name="refresh" size={12} className="animate-spin" /> : "Add endpoint"}
              </Button>
            </div>
          </div>

          {webhooks.length > 0 && (
            <DataTable
              columns={webhookColumns}
              rows={webhooks}
              rowKey={(w) => w.id}
              minWidth={560}
            />
          )}

          <p className="font-sans text-[11px] leading-relaxed text-muted-foreground">
            Each request is a JSON <code className="font-mono">alert.created</code> event signed with{" "}
            <code className="font-mono">Peersheep-Signature: t=…,v1=…</code>, an HMAC-SHA256 of <code className="font-mono">t.body</code>.
          </p>
        </SettingsBlock>
      </SettingsSection>

      {/* Signing secret */}
      <Dialog open={!!shownSecret} onOpenChange={(open) => !open && setShownSecret(null)}>
        <DialogContent className={SETTINGS_BUTTONS}>
          <DialogHeader>
            <DialogTitle>Signing secret</DialogTitle>
          </DialogHeader>
          <p className="font-sans text-xs text-muted-foreground">
            Use this to check that requests really come from Peersheep. Keep it private.
          </p>
          <div className="flex gap-2">
            <Input value={shownSecret ?? ""} readOnly className="font-mono text-[11px]" />
            <Button
              variant="outline"
              onClick={() => {
                if (shownSecret) void navigator.clipboard.writeText(shownSecret);
                toast.success("Secret copied");
              }}
              aria-label="Copy secret"
            >
              <Icon name="copy-01" size={13} />
            </Button>
          </div>
          <DialogFooter>
            <Button onClick={() => setShownSecret(null)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm disconnect */}
      <Dialog open={!!disconnecting} onOpenChange={(open) => !open && setDisconnecting(null)}>
        <DialogContent className={SETTINGS_BUTTONS}>
          <DialogHeader>
            <DialogTitle>Disconnect {disconnecting === "slack" ? "Slack" : "Linear"}?</DialogTitle>
          </DialogHeader>
          <p className="font-sans text-xs text-muted-foreground">
            {disconnecting === "slack"
              ? "Alerts stop posting to Slack. You can add it again any time."
              : "Peersheep stops filing issues. Issues already created stay in Linear."}
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDisconnecting(null)}>Cancel</Button>
            <Button variant="destructive" onClick={() => void disconnect()}>Disconnect</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm webhook delete */}
      <Dialog open={!!deletingHook} onOpenChange={(open) => !open && setDeletingHook(null)}>
        <DialogContent className={SETTINGS_BUTTONS}>
          <DialogHeader>
            <DialogTitle>Delete this webhook?</DialogTitle>
          </DialogHeader>
          <p className="break-all font-mono text-[11px] text-muted-foreground">{deletingHook?.url}</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeletingHook(null)}>Cancel</Button>
            <Button variant="destructive" onClick={() => void deleteWebhook()}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SettingsPane>
  );
}

function ErrorBlock({ message }: { message: string }) {
  return (
    <SettingsBlock className="flex items-start gap-2 bg-red-500/[0.04]">
      <Icon name="alert-circle" size={13} className="mt-px text-red-500" />
      <p className="font-sans text-xs text-red-500">{message}</p>
    </SettingsBlock>
  );
}

function IntegrationsSkeleton() {
  return (
    <div className="space-y-6" aria-busy="true">
      <div className="space-y-2">
        <Skeleton className="h-5 w-32" />
        <Skeleton className="h-3 w-64" />
      </div>
      {[2, 2, 1].map((rows, i) => (
        <div key={i} className="overflow-hidden rounded-lg border border-border bg-card">
          <div className="flex items-center justify-between border-b border-border px-5 py-4">
            <div className="flex items-center gap-3">
              <Skeleton className="h-9 w-9 rounded-md" />
              <div className="space-y-1.5">
                <Skeleton className="h-3.5 w-20" />
                <Skeleton className="h-3 w-56" />
              </div>
            </div>
            <Skeleton className="h-8 w-24 rounded-md" />
          </div>
          {Array.from({ length: rows }, (_, r) => (
            <div key={r} className="flex items-center justify-between border-t border-border px-5 py-4 first:border-t-0">
              <div className="space-y-1.5">
                <Skeleton className="h-3 w-16" />
                <Skeleton className="h-3 w-48" />
              </div>
              <Skeleton className="h-8 w-44 rounded-md" />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
